import { useState } from "react";
import { OfflineBasemapNote } from "./OfflineBasemapNote";

export interface QualityOption { id: string; label: string }

export interface SavestateProgress {
  done: number;
  total: number;
  label: string;
}

export interface SavestateDialogProps {
  qualities: QualityOption[];
  progress?: SavestateProgress | null;
  busy: boolean;
  /** When set, the dialog shows this message instead of the picker/progress. */
  error?: string | null;
  onBuild: (qualityId: string) => void;
  onClose: () => void;
  /** Called by Cancel while progress is active. Cancel is disabled if absent. */
  onCancel?: () => void;
  /** Shown as a Retry button in the error state when present. */
  onRetry?: () => void;
}

export function SavestateDialog(props: SavestateDialogProps): JSX.Element {
  const [chosen, setChosen] = useState(props.qualities[0]?.id ?? "");
  const progress = props.progress ?? null;
  const error = props.error ?? null;
  const inProgress = error === null && progress !== null;
  const picking = error === null && !inProgress;
  const title = error !== null
    ? "Download for offline use — failed"
    : inProgress
      ? "Download for offline use"
      : "Download for offline use — choose video quality";
  return (
    <div role="dialog" aria-label="Download for offline use"
      style={{ position: "fixed", inset: 0, display: "grid", placeItems: "center",
        background: "rgba(0,0,0,0.6)", zIndex: 2000 }}>
      <div style={{ background: "#1a1a1a", color: "#eee", padding: 20, minWidth: 320 }}>
        <h3>{title}</h3>
        {error !== null && (
          <p role="alert" style={{ color: "#f88" }}>{error}</p>
        )}
        {picking && (
          <>
            <ul style={{ listStyle: "none", padding: 0 }}>
              {props.qualities.map((q) => (
                <li key={q.id}>
                  <label>
                    <input type="radio" name="quality" value={q.id}
                      checked={chosen === q.id} onChange={() => setChosen(q.id)} />
                    {" "}{q.label}
                  </label>
                </li>
              ))}
            </ul>
            <OfflineBasemapNote />
          </>
        )}
        {inProgress && progress !== null && (
          <div>
            <p>{progress.done} of {progress.total} — {progress.label}</p>
            <progress value={progress.done} max={progress.total}
              style={{ width: "100%" }} />
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {error !== null && props.onRetry !== undefined && (
            <button type="button" onClick={props.onRetry}>Retry</button>
          )}
          {error !== null && (
            <button type="button" onClick={props.onClose}>Close</button>
          )}
          {inProgress && (
            // Cancel aborts the in-flight download via the owner's
            // AbortController; already-cached assets persist for resumption.
            <button type="button" disabled={props.onCancel === undefined}
              onClick={props.onCancel}>
              Cancel
            </button>
          )}
          {picking && (
            <>
              <button type="button" onClick={props.onClose}>Cancel</button>
              <button type="button" disabled={props.busy}
                onClick={() => props.onBuild(chosen)}>
                {props.busy ? "Building…" : "Build"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
